<template>
    <div class="ppb">

        <!-- Progress header -->
        <div class="ppb-header">
            <div class="ppb-header-l">
                <div class="ppb-icon" :style="`background:rgba(${primaryRgb},0.12);border-color:rgba(${primaryRgb},0.22);color:${primaryColor};`">
                    <svg width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.2" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                    </svg>
                </div>
                <div>
                    <p class="ppb-title">Profit Progress</p>
                    <p class="ppb-sub">
                        Total Earned:
                        <span :style="`color:${primaryColor};`">{{ fmtProfit(progressData.total_profit_earned, progressData.total_profit_earned_btc_raw || progressData.total_profit_earned_btc) }}</span>
                        / {{ fmtProfit(progressData.max_profit, progressData.max_profit_btc_raw || progressData.max_profit_btc) }}
                    </p>
                </div>
            </div>
            <div class="ppb-pct" :style="`color:${getPctColor()};`">
                {{ progressData.profit_percentage.toFixed(1) }}%
            </div>
        </div>

        <!-- Progress bar -->
        <div class="ppb-track">
            <div class="ppb-zone-70"></div>
            <div
                class="ppb-fill"
                :style="`width:${Math.min(progressData.profit_percentage,100)}%;background:${getBarGrad()};`"
            >
                <div class="ppb-shine"></div>
            </div>
            <div class="ppb-marker-70"></div>
            <span class="ppb-track-label">{{ progressData.profit_percentage.toFixed(1) }}%</span>
        </div>
        <div class="ppb-track-ends">
            <span>0%</span>
            <span>70%</span>
            <span>100%</span>
        </div>

        <!-- Warnings -->
        <div v-if="progressData.is_nearing_cap && !progressData.profit_cap_reached" class="ppb-warn ppb-warn--yellow">
            <svg class="ppb-warn-ic" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
            </svg>
            <div>
                <p class="ppb-warn-title">Approaching Profit Cap</p>
                <p class="ppb-warn-desc">
                    {{ progressData.profit_percentage.toFixed(1) }}% of limit reached.
                    Remaining: {{ fmtProfit(progressData.remaining_capacity, progressData.remaining_capacity_btc_raw || progressData.remaining_capacity_btc) }}
                </p>
            </div>
        </div>

        <div v-if="progressData.profit_cap_reached" class="ppb-warn ppb-warn--red">
            <svg class="ppb-warn-ic" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
            </svg>
            <div>
                <p class="ppb-warn-title">Profit Cap Reached — Payouts Stopped</p>
                <p class="ppb-warn-desc">Maximum limit hit. <strong>Reinvestment required to continue earning.</strong></p>
                <p v-if="progressData.profit_capped_at" class="ppb-warn-date">Cap reached: {{ fmtDate(progressData.profit_capped_at) }}</p>
            </div>
        </div>

        <!-- Breakdown grid -->
        <div class="ppb-breakdown">
            <p class="ppb-breakdown-title">Profit Breakdown</p>
            <div class="ppb-grid">
                <div class="ppb-stat">
                    <span class="ppb-stat-lbl">ROI Earned</span>
                    <span class="ppb-stat-val ppb-stat-val--green">{{ fmtProfit(progressData.roi_earned, progressData.roi_earned_btc_raw || progressData.roi_earned_btc) }}</span>
                </div>
                <div class="ppb-stat">
                    <span class="ppb-stat-lbl">Referral Commissions</span>
                    <span class="ppb-stat-val ppb-stat-val--blue">{{ fmt(progressData.referral_commissions_earned) }}</span>
                </div>
                <div class="ppb-stat">
                    <span class="ppb-stat-lbl">Total Profit</span>
                    <span class="ppb-stat-val" :style="`color:${primaryColor};`">{{ fmtProfit(progressData.total_profit_earned, progressData.total_profit_earned_btc_raw || progressData.total_profit_earned_btc) }}</span>
                </div>
                <div class="ppb-stat">
                    <span class="ppb-stat-lbl">Remaining Capacity</span>
                    <span class="ppb-stat-val" :class="progressData.remaining_capacity > 0 ? 'ppb-stat-val--muted' : 'ppb-stat-val--red'">
                        {{ fmt(progressData.remaining_capacity) }}
                    </span>
                </div>
            </div>
        </div>

        <!-- Reinvest CTA -->
        <div v-if="progressData.profit_cap_reached && showReinvestButton" class="ppb-cta">
            <button
                class="ppb-cta-btn"
                :style="`background:linear-gradient(135deg,#7c3aed,${primaryColor});box-shadow:0 4px 18px rgba(${primaryRgb},0.35);`"
                @click="$emit('reinvest')"
            >
                <svg width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                </svg>
                Reinvest to Continue Earning
            </button>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
    progressData: {
        type: Object,
        required: true,
        default: () => ({
            max_profit: 0,
            roi_earned: 0,
            referral_commissions_earned: 0,
            total_profit_earned: 0,
            profit_percentage: 0,
            remaining_capacity: 0,
            profit_cap_reached: false,
            is_nearing_cap: false,
            profit_capped_at: null
        })
    },
    showReinvestButton: { type: Boolean, default: true },
    primaryColor: { type: String, default: '#86efac' }
})

defineEmits(['reinvest'])

const hexToRgb = (hex) => {
    const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return r ? `${parseInt(r[1],16)}, ${parseInt(r[2],16)}, ${parseInt(r[3],16)}` : '134, 239, 172'
}

const primaryRgb = computed(() => hexToRgb(props.primaryColor))
const hasBtcDisplay = computed(() => Number(props.progressData.btc_amount || 0) > 0 && Number(props.progressData.btc_price_usd || 0) > 0)

const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(v || 0)
const fmtBtc = (v, precision = 8) => `₿${new Intl.NumberFormat('en-US', { minimumFractionDigits: precision, maximumFractionDigits: precision }).format(v || 0)}`
const fmtProfit = (usdValue, btcValue) => {
    if (hasBtcDisplay.value && btcValue !== null && btcValue !== undefined) {
        const precision = 12  // Rio wallet always uses 12 decimals
        return fmtBtc(btcValue, precision)
    }

    return fmt(usdValue)
}

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'

const getPctColor = () => {
    const p = props.progressData.profit_percentage
    if (p >= 100) return '#f87171'
    if (p >= 70)  return '#fbbf24'
    return props.primaryColor
}

const getBarGrad = () => {
    const p = props.progressData.profit_percentage
    if (p >= 100) return 'linear-gradient(90deg,#dc2626,#f87171)'
    if (p >= 70)  return 'linear-gradient(90deg,#d97706,#fbbf24)'
    return `linear-gradient(90deg,#7c3aed,${props.primaryColor})`
}
</script>

<style scoped>
.ppb {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

/* ── Header ── */
.ppb-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
}
.ppb-header-l {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
}
.ppb-icon {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    border: 1px solid;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}
.ppb-title {
    font-size: 12.5px;
    font-weight: 700;
    color: #fff;
    margin: 0 0 2px;
}
.ppb-sub {
    font-size: 11px;
    color: rgba(148,163,184,0.6);
    margin: 0;
}
.ppb-pct {
    font-size: 18px;
    font-weight: 800;
    line-height: 1;
    flex-shrink: 0;
}

/* ── Track ── */
.ppb-track {
    position: relative;
    width: 100%;
    height: 20px;
    background: rgba(255,255,255,0.07);
    border-radius: 50px;
    overflow: hidden;
}
.ppb-zone-70 {
    position: absolute;
    top: 0; left: 0; width: 70%; height: 100%;
    background: rgba(34,197,94,0.04);
    pointer-events: none;
}
.ppb-fill {
    position: absolute;
    top: 0; left: 0; height: 100%;
    border-radius: 50px;
    transition: width 0.6s ease;
    overflow: hidden;
}
.ppb-shine {
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg,transparent,rgba(255,255,255,0.22),transparent);
    animation: ppbShine 2.4s infinite;
}
@keyframes ppbShine {
    0%   { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
}
.ppb-marker-70 {
    position: absolute;
    top: 0; left: 70%; width: 2px; height: 100%;
    background: rgba(251,191,36,0.45);
    pointer-events: none;
}
.ppb-track-label {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 700;
    color: rgba(255,255,255,0.85);
    text-shadow: 0 1px 3px rgba(0,0,0,0.6);
    pointer-events: none;
}
.ppb-track-ends {
    display: flex;
    justify-content: space-between;
    font-size: 10px;
    color: rgba(148,163,184,0.4);
    font-weight: 600;
    margin-top: -4px;
    padding: 0 2px;
}

/* ── Warnings ── */
.ppb-warn {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px 14px;
    border-radius: 10px;
    border: 1px solid;
}
.ppb-warn--yellow {
    background: rgba(251,191,36,0.07);
    border-color: rgba(251,191,36,0.22);
}
.ppb-warn--red {
    background: rgba(239,68,68,0.07);
    border-color: rgba(239,68,68,0.22);
}
.ppb-warn-ic {
    width: 15px;
    height: 15px;
    flex-shrink: 0;
    margin-top: 1px;
}
.ppb-warn--yellow .ppb-warn-ic { color: #fbbf24; }
.ppb-warn--red    .ppb-warn-ic { color: #f87171; }
.ppb-warn-title {
    font-size: 11.5px;
    font-weight: 700;
    margin: 0 0 3px;
}
.ppb-warn--yellow .ppb-warn-title { color: #fde68a; }
.ppb-warn--red    .ppb-warn-title { color: #fca5a5; }
.ppb-warn-desc {
    font-size: 10.5px;
    color: rgba(148,163,184,0.65);
    margin: 0;
    line-height: 1.45;
}
.ppb-warn--yellow .ppb-warn-desc { color: rgba(253,230,138,0.65); }
.ppb-warn--red    .ppb-warn-desc { color: rgba(252,165,165,0.65); }
.ppb-warn-date {
    font-size: 10px;
    color: rgba(248,113,113,0.55);
    margin: 4px 0 0;
}

/* ── Breakdown ── */
.ppb-breakdown {
    background: rgba(255,255,255,0.025);
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 12px;
    padding: 12px 14px;
}
.ppb-breakdown-title {
    font-size: 10.5px;
    font-weight: 700;
    color: rgba(148,163,184,0.5);
    text-transform: uppercase;
    letter-spacing: 0.07em;
    margin: 0 0 10px;
}
.ppb-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px 16px;
}
.ppb-stat {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
}
.ppb-stat-lbl {
    font-size: 10px;
    color: rgba(148,163,184,0.5);
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.ppb-stat-val {
    font-size: 13px;
    font-weight: 800;
    color: rgba(226,232,240,0.9);
}
.ppb-stat-val--green  { color: #4ade80; }
.ppb-stat-val--blue   { color: #60a5fa; }
.ppb-stat-val--red    { color: #f87171; }
.ppb-stat-val--muted  { color: rgba(226,232,240,0.65); }

/* ── Reinvest CTA ── */
.ppb-cta { width: 100%; }
.ppb-cta-btn {
    width: 100%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px 18px;
    border-radius: 10px;
    border: none;
    color: #fff;
    font-size: 12.5px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s ease;
    letter-spacing: 0.02em;
}
.ppb-cta-btn:hover {
    transform: translateY(-1px);
    filter: brightness(1.1);
}

@media (max-width: 480px) {
    .ppb-grid { grid-template-columns: 1fr 1fr; gap: 8px 12px; }
    .ppb-stat-val { font-size: 12px; }
    .ppb-pct { font-size: 16px; }
}
</style>
